import type { ReactNode } from "react";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

/**
 * Shared by CoveragesEditor/LoadingsEditor: one row per (category, numeric
 * value) pair picked from a fixed catalog (land uses / pollutants), with an
 * "add next available" button and per-row delete. Generic over accessor
 * functions rather than a fixed `{category, value}` shape, since `Coverage`/
 * `Loading` keep their own domain field names (`landUse`/`percent` vs
 * `pollutant`/`initialBuildup`) rather than being renamed to match.
 */
export function CatalogPickerTable<T>({
  rows,
  catalog,
  onChange,
  getCategory,
  getValue,
  withCategory,
  withValue,
  newRow,
  title,
  categoryHeader,
  valueHeader,
  emptyCatalogHint,
  emptyRowsHint,
  addLabel,
  deleteLabel,
  footer,
}: {
  rows: T[];
  catalog: { name: string }[];
  onChange: (rows: T[]) => void;
  getCategory: (row: T) => string;
  getValue: (row: T) => number;
  withCategory: (row: T, category: string) => T;
  withValue: (row: T, value: number) => T;
  newRow: (category: string) => T;
  title: string;
  categoryHeader: string;
  valueHeader: string;
  emptyCatalogHint: string;
  emptyRowsHint: string;
  addLabel: string;
  deleteLabel: string;
  footer?: (rows: T[]) => ReactNode;
}) {
  const usedNames = new Set(rows.map(getCategory));
  const availableToAdd = catalog.filter((c) => !usedNames.has(c.name));

  function addRow() {
    if (availableToAdd.length === 0) return;
    onChange([...rows, newRow(availableToAdd[0].name)]);
  }

  function setCategory(oldCategory: string, category: string) {
    onChange(rows.map((r) => (getCategory(r) === oldCategory ? withCategory(r, category) : r)));
  }

  function setValue(category: string, value: number) {
    onChange(rows.map((r) => (getCategory(r) === category ? withValue(r, value) : r)));
  }

  function removeRow(category: string) {
    onChange(rows.filter((r) => getCategory(r) !== category));
  }

  if (catalog.length === 0) {
    return (
      <section className="property-panel-group">
        <h3>{title}</h3>
        <p className="property-panel-hint">{emptyCatalogHint}</p>
      </section>
    );
  }

  return (
    <section className="property-panel-group">
      <h3>{title}</h3>
      {rows.length === 0 && <p className="property-panel-hint">{emptyRowsHint}</p>}
      {rows.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{categoryHeader}</th>
                <th>{valueHeader}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const category = getCategory(row);
                return (
                  <tr key={category}>
                    <td>
                      <select
                        value={category}
                        onChange={(e) => setCategory(category, e.target.value)}
                      >
                        <option value={category}>{category}</option>
                        {availableToAdd.map((c) => (
                          <option key={c.name} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <NumberInput value={getValue(row)} onChange={(v) => setValue(category, v)} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="button-icon button-icon-danger"
                        onClick={() => removeRow(category)}
                        aria-label={deleteLabel}
                        title={deleteLabel}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && footer?.(rows)}
      <button className="button" onClick={addRow} disabled={availableToAdd.length === 0}>
        {addLabel}
      </button>
    </section>
  );
}
